import React, { useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import sendRequest from '../helpers/sendRequest';
import Api from '../api';
import {
  cancellationReasonsFiltersAtom,
  cancellationReasonsInfoAtom,
  gettingCancellationReasonsLoadingAtom,
  savingCancellationReasonLoadingAtom,
  deletingCancellationReasonLoadingAtom,
} from '../atoms/cancellationReasonsAtoms';
import { modalStateAtom, closeModalAtom, openInfoModalAtom } from '../atoms/globalAtoms';
import LoadingModal from '../components/LoadingModal';
import CancellationReasonsServices from '../services/CancellationReasonsServices';

const UseCancellationReasons = () => {
  const [filters, setFilters] = useAtom(cancellationReasonsFiltersAtom);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const [, setInfo] = useAtom(cancellationReasonsInfoAtom);
  const [gettingLoading, setGettingLoading] = useAtom(gettingCancellationReasonsLoadingAtom);
  const [savingLoading, setSavingLoading] = useAtom(savingCancellationReasonLoadingAtom);
  const [deletingLoading, setDeletingLoading] = useAtom(deletingCancellationReasonLoadingAtom);
  const [, setModalState] = useAtom(modalStateAtom);
  const [, closeModal] = useAtom(closeModalAtom);
  const [, openInfoModal] = useAtom(openInfoModalAtom);

  const getCancellationReasons = async (filtersOverride) => {
    const currentFilters = filtersOverride ?? filtersRef.current;
    const params = CancellationReasonsServices.convertTableFilters(currentFilters);
    const data = await sendRequest({
      request: () => Api.getCancellationReasons(params),
      payload: null,
      warnErrorText: 'while getting cancellation reasons',
      setLoading: setGettingLoading,
      errorText: 'Не удалось загрузить причины отмены',
    });

    if (data?.status !== 'ok') return;
    setInfo(data.data);
  };

  const createCancellationReason = async (name) => {
    const data = await sendRequest({
      request: Api.createCancellationReason,
      payload: { name: name.trim() },
      warnErrorText: 'while creating cancellation reason',
      setLoading: setSavingLoading,
      errorText: 'Не удалось создать причину отмены',
    });

    if (data?.status !== 'ok') return false;
    closeModal();
    openInfoModal({ title: 'Готово', text: 'Причина отмены создана' });
    await getCancellationReasons();
    return true;
  };

  const updateCancellationReason = async (id, name) => {
    const data = await sendRequest({
      request: (payload) => Api.updateCancellationReason(payload.id, { name: payload.name }),
      payload: { id, name: name.trim() },
      warnErrorText: 'while updating cancellation reason',
      setLoading: setSavingLoading,
      errorText: 'Не удалось обновить причину отмены',
    });

    if (data?.status !== 'ok') return false;
    closeModal();
    openInfoModal({ title: 'Готово', text: 'Причина отмены обновлена' });
    await getCancellationReasons();
    return true;
  };

  const deleteCancellationReason = async (id) => {
    const data = await sendRequest({
      request: (payload) => Api.deleteCancellationReason(payload.id),
      payload: { id },
      warnErrorText: 'while deleting cancellation reason',
      setLoading: setDeletingLoading,
      errorText: 'Не удалось удалить причину отмены',
    });

    if (data?.status !== 'ok') return false;
    closeModal();
    openInfoModal({ title: 'Готово', text: 'Причина отмены удалена' });
    await getCancellationReasons();
    return true;
  };

  const debounceTimerRef = useRef(null);

  useEffect(() => {
    debounceTimerRef.current = setTimeout(() => {
      getCancellationReasons(filtersRef.current);
    }, 500);

    return () => clearTimeout(debounceTimerRef.current);
  }, [filters.date]);

  useEffect(() => {
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    getCancellationReasons(filtersRef.current);
  }, [filters.sortKey, filters.sortType, filters.page]);

  useEffect(() => {
    if (!gettingLoading) return closeModal();
    setModalState({
      isOpen: true,
      closeOnPressOutside: false,
      children: <LoadingModal />,
    });
  }, [gettingLoading]);

  return {
    getCancellationReasons,
    createCancellationReason,
    updateCancellationReason,
    deleteCancellationReason,
    setFilters,
    savingLoading,
    deletingLoading,
  };
};

export default UseCancellationReasons;
